import React from 'react';
import { usePublicCompany } from '../hooks/usePublicCompany';
import LegalFooter from '../components/LegalFooter';

/**
 * Pagina pubblica con i Termini e Condizioni d'uso (EULA) dell'app, incluse le
 * clausole minime richieste da Apple per chi non usa lo Standard EULA.
 * I dati del fornitore arrivano da /api/public/company (Impostazioni → Dati Agenzia).
 */

const UPDATED = '25 agosto 2026';

const Section: React.FC<{ n: string; title: string; children: React.ReactNode }> = ({ n, title, children }) => (
    <section className="mb-8">
        <h2 className="text-lg font-black text-slate-900 mb-3">
            <span className="text-rose-600">{n}.</span> {title}
        </h2>
        <div className="space-y-3 text-[14px] leading-relaxed text-slate-700">{children}</div>
    </section>
);

const TerminiCondizioniView: React.FC = () => {
    const company = usePublicCompany();
    const CONTACT_EMAIL = company.email;
    const identity = `${company.agencyName}${company.fullAddress ? `, ${company.fullAddress}` : ''}${company.vatNumber ? `, P.IVA ${company.vatNumber}` : ''}`;
    return (
        <div className="min-h-screen bg-slate-50 py-10 px-4">
            <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-8 sm:p-12">
                <header className="mb-8 pb-6 border-b border-slate-200">
                    <p className="text-[11px] font-black uppercase tracking-widest text-rose-600 mb-2">Ultra Detective</p>
                    <h1 className="text-2xl sm:text-3xl font-black text-slate-900">Termini e Condizioni d'Uso</h1>
                    <p className="text-[13px] text-slate-500 mt-2">Ultimo aggiornamento: {UPDATED}</p>
                </header>

                <p className="text-[14px] leading-relaxed text-slate-700 mb-8">
                    I presenti Termini e Condizioni (il “Contratto”) regolano l'uso dell'applicazione mobile e della
                    piattaforma web <strong>Ultra Detective</strong> (il “Servizio”), forniti da
                    <strong> {company.agencyName}</strong>{identity !== company.agencyName ? ` (${identity})` : ''} (il
                    “Fornitore”). Utilizzando il Servizio l'utente accetta integralmente il presente Contratto.
                </p>

                <Section n="1" title="Oggetto e accettazione">
                    <p>
                        Il Servizio è un gestionale <strong>ad uso professionale</strong> destinato ad agenzie
                        investigative e di vigilanza privata autorizzate e ai loro operatori. L'accesso è riservato
                        agli utenti autorizzati dalla propria agenzia mediante credenziali e codice azienda. L'uso del
                        Servizio implica l'accettazione dei presenti Termini e dell'Informativa Privacy.
                    </p>
                </Section>

                <Section n="2" title="Licenza d'uso">
                    <p>
                        Il Fornitore concede all'utente una licenza <strong>personale, limitata, non esclusiva, non
                        trasferibile e revocabile</strong> a utilizzare l'App sui dispositivi di sua proprietà o
                        controllo, esclusivamente per le finalità professionali consentite. Nessun diritto di
                        proprietà intellettuale è ceduto all'utente.
                    </p>
                </Section>

                <Section n="3" title="Account, credenziali e sicurezza">
                    <p>
                        L'utente è responsabile della riservatezza delle proprie credenziali e di ogni attività svolta
                        tramite il proprio account. L'App offre uno sblocco biometrico facoltativo, la cui elaborazione
                        avviene solo sul dispositivo. L'utente deve comunicare tempestivamente qualsiasi uso non
                        autorizzato del proprio account.
                    </p>
                </Section>

                <Section n="4" title="Uso consentito e divieti">
                    <p>L'utente si impegna a utilizzare il Servizio nel rispetto della legge e, in particolare, a NON:</p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li>utilizzare il Servizio per finalità illecite o in violazione dei diritti di terzi;</li>
                        <li>trattare dati personali di terzi (clienti, soggetti di indagine) in assenza di idonea base giuridica e delle informative richieste dalla normativa;</li>
                        <li>tentare di decompilare, disassemblare o effettuare reverse engineering del software, salvo nei limiti inderogabili di legge;</li>
                        <li>compromettere la sicurezza o l'integrità del Servizio o accedere a dati di agenzie diverse dalla propria.</li>
                    </ul>
                </Section>

                <Section n="5" title="Contenuti dell'utente">
                    <p>
                        I contenuti caricati o generati dall'utente (prove fotografiche/video, relazioni, note, spese e
                        dati inseriti) restano di titolarità dell'utente e/o della sua agenzia, che ne è unico
                        responsabile. Il Fornitore non esercita controllo editoriale su tali contenuti e non ne
                        risponde. L'utente garantisce di avere il diritto di trattare i dati che inserisce.
                    </p>
                </Section>

                <Section n="6" title="Proprietà intellettuale">
                    <p>
                        Il software, il marchio “Ultra Detective”, il codice, la grafica e ogni componente del Servizio
                        sono di titolarità del Fornitore o dei suoi licenzianti e sono protetti dalle leggi vigenti.
                        Nulla nel presente Contratto trasferisce all'utente tali diritti.
                    </p>
                </Section>

                <Section n="7" title="Disponibilità, aggiornamenti e manutenzione">
                    <p>
                        Il Fornitore si adopera per garantire la continuità del Servizio ma non ne garantisce la
                        disponibilità ininterrotta. Il Servizio può essere aggiornato, sospeso o modificato per
                        esigenze tecniche, di sicurezza o normative. La responsabilità dell'assistenza e della
                        manutenzione dell'App è del Fornitore; Apple non ha alcun obbligo di manutenzione o assistenza.
                    </p>
                </Section>

                <Section n="8" title="Esclusione di garanzie">
                    <p>
                        Nei limiti consentiti dalla legge, il Servizio è fornito “<strong>così com'è</strong>” e “come
                        disponibile”, senza garanzie di alcun tipo, espresse o implicite, in merito ad assenza di
                        errori, idoneità a uno scopo specifico o adeguatezza dei contenuti generati. Restano ferme le
                        garanzie inderogabili di legge a favore dei consumatori, ove applicabili.
                    </p>
                </Section>

                <Section n="9" title="Limitazione di responsabilità">
                    <p>
                        Nei limiti di legge, il Fornitore non risponde dei danni indiretti, consequenziali o della
                        perdita di dati derivanti dall'uso o dall'impossibilità di uso del Servizio, né dell'uso che
                        l'utente o l'agenzia fanno dei contenuti e dei dati trattati tramite il Servizio.
                    </p>
                </Section>

                <Section n="10" title="Durata e cessazione">
                    <p>
                        Il Contratto è efficace finché l'utente utilizza il Servizio. Il Fornitore può sospendere o
                        revocare l'accesso in caso di violazione dei presenti Termini. L'utente può cessare l'uso in
                        qualsiasi momento disinstallando l'App; la conservazione dei dati resta regolata
                        dall'Informativa Privacy e dagli accordi tra Fornitore e agenzia.
                    </p>
                </Section>

                <Section n="11" title="Condizioni specifiche per Apple App Store">
                    <p>Per le App scaricate tramite Apple App Store si applicano inoltre le seguenti condizioni:</p>
                    <ul className="list-disc pl-5 space-y-2">
                        <li>il presente Contratto è concluso <strong>esclusivamente tra l'utente e il Fornitore</strong>, non con Apple; il Fornitore è l'unico responsabile dell'App e dei suoi contenuti;</li>
                        <li>Apple non ha alcun obbligo di fornire assistenza o manutenzione relativa all'App;</li>
                        <li>in caso di mancata conformità dell'App a una garanzia applicabile, l'utente può richiedere ad Apple il rimborso del prezzo eventualmente pagato; per il resto, nella misura massima consentita dalla legge, Apple non ha altri obblighi di garanzia;</li>
                        <li>il Fornitore — e non Apple — è responsabile di eventuali reclami relativi all'App (responsabilità da prodotto, non conformità a requisiti di legge, tutela dei consumatori) e delle rivendicazioni di terzi secondo cui l'App violerebbe diritti di proprietà intellettuale;</li>
                        <li>l'utente dichiara di non trovarsi in un Paese soggetto a embargo del Governo statunitense o designato come “terrorist supporting” e di non figurare in liste di soggetti vietati o soggetti a restrizioni;</li>
                        <li><strong>Apple e le sue controllate sono terzi beneficiari</strong> del presente Contratto e hanno il diritto di farlo valere nei confronti dell'utente.</li>
                    </ul>
                </Section>

                <Section n="12" title="Legge applicabile e foro competente">
                    <p>
                        Il presente Contratto è regolato dalla legge italiana. Per ogni controversia è competente il
                        Foro del luogo in cui ha sede il Fornitore{company.city ? ` (${company.city})` : ''}, fatte
                        salve le disposizioni inderogabili a tutela del consumatore ove applicabili.
                    </p>
                </Section>

                <Section n="13" title="Contatti">
                    <p>
                        {identity} —
                        <a className="text-rose-600 underline" href={`mailto:${CONTACT_EMAIL}`}> {CONTACT_EMAIL}</a>
                    </p>
                </Section>

                <footer className="mt-10 pt-6 border-t border-slate-200 text-[12px] text-slate-400">
                    {company.agencyName} — Termini e Condizioni — {UPDATED}
                </footer>

                <LegalFooter />
            </div>
        </div>
    );
};

export default TerminiCondizioniView;
